import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid2 from '@mui/material/Grid2';
import { useAlert } from '../../../../components/alertProvider';
import { Formik, Form } from 'formik';
import { RequiredLabel } from '../../../../utils/commonUtils';
import { AdvanceConfigLookups, advanceInitialValues, AdvancePaymentPayload, getbillingConfigURL, validationSchema, MakePayment, PaymentDetailPayload, postMakeAdvancePaymentURL } from '../advance.helper';
import { useNavigate, useParams } from 'react-router-dom';
import { usePatientContext } from '../../../../components/patinetProvider';
import { useAppQuery } from '../../../../services/appQuery';
import { getRequest, postRequest } from '../../../../services/httpService';
import CollectPaymentModal from '../../collectPayment/CollectPaymentModal';
import { PaymentDetails, PaymentFormValues, PaymentHistoryItem } from '../../collectPayment/collectPayment.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import useCurrency from '../../../../components/CurrencyProvider';
import { IconButton } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { ReactComponent as ViewIcon } from '../../../../assets/SVG/viewIconSvg.svg';
import { useTranslation } from 'react-i18next';

const CheckedInPage: React.FC = () => {
  const navigate = useNavigate();
  const { appointment_id } = useParams<{ id: string; appointment_id: string }>();
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [latestFormValues, setLatestFormValues] = useState<AdvancePaymentPayload | null>(null);
  const queryClient = useQueryClient();
  const { t : tBilling } = useTranslation('billing_config');
  const { currency } = useCurrency();

  const { patientId , MainpatientDetails } = usePatientContext();
  const { addAlert } = useAlert();

  const { data: billingConfigAdvance } = useAppQuery<AdvanceConfigLookups>(
    ['billingConfigAdvance'],
    () => getRequest<AdvanceConfigLookups>(getbillingConfigURL()),
    { staleTime: 5 * 60 * 1000 }
  );

  const { mutate: submitPayment, isPending: isSubmittingPayment } = useMutation({
    mutationFn: (payload: MakePayment) => postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      addAlert(tBilling('Advance.messages.payment_saved_success', { defaultValue: 'Payment saved successfully.' }), 'success');
      setCollectPaymentOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['checkedInPatients'] });
      navigate(`/admin/billing/advance/patient-advance/${patientId}?checked-in`);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('Advance.messages.payment_save_failed', { defaultValue: 'Failed to save payment.' });
      addAlert(message, 'error');
    },
  });
  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[] = []) => {
    if (!latestFormValues) {
      addAlert(tBilling('Advance.messages.missing_advance_form_values', { defaultValue: 'Missing advance form values. Please try again.' }), 'error');
      return;
    }
    if (!history.length) {
      addAlert(tBilling('Advance.messages.missing_payment_history', { defaultValue: 'Missing payment history. Please try again.' }), 'error');
      return;
    }

    const paymentCurrency = paymentDetailsForModal?.currency ?? currency;
    const isoOrNull = (d?: string) => (d ? new Date(d).toISOString() : new Date().toISOString());

    const payment_details: PaymentDetailPayload[] = history.map((item) => {
      const base: PaymentDetailPayload = {
        payment_mode_id: item.payment_mode_id ?? 0,
        paid_amount: String(item.amount ?? 0),
        currency: paymentCurrency,
        number: null,
        date: isoOrNull(item.date),
        bank_id: null,
        account_name: null,
        branch_name: null,
        ifsc_code: null,
        remarks: item.notes ?? null,
        cheque_number: null,
        cheque_date: null,
      };

      // Map numbers/cheque specifics based on method
      if (item.method?.toLowerCase().includes('bank') || item.method?.toLowerCase().includes('online') || item.method?.toLowerCase().includes('card') || item.method?.toLowerCase().includes('upi')) {
        base.number = item.transactionNumber ?? item.cardTransactionNumber ?? item.transactionNumber ?? null;
      }

      if (item.method?.toLowerCase().includes('cheque')) {
        base.cheque_number = item.chequeNumber ?? null;
        base.cheque_date = item.chequeDate ? new Date(item.chequeDate).toISOString() : null;
      }

      return base;
    });

    const totalPaid = payment_details.reduce((sum, p) => sum + (Number(p.paid_amount) || 0), 0);

    // Use display name for advance_against from config when available
    const againstName = latestFormValues.advance_against
      ? (billingConfigAdvance?.ADVANCE_AGAINST || []).find((opt) => opt.code === latestFormValues.advance_against)?.name || latestFormValues.advance_against
      : '';

    const payload: MakePayment = {
      invoice_id: null,
      patient_id: Number(patientId),
      clinic_id: MainpatientDetails?.clinic ?? 0,
      payment_against_type: 'ADVANCE',
      advance_data: {
        ...latestFormValues,
        advance_against: againstName,
        amount: totalPaid > 0 ? totalPaid : latestFormValues.amount,
      },
      remarks: latestFormValues.remark ?? '',
      payment_details,
    };

    submitPayment(payload);
  };


  const handleOpenCollectPayment = (netAmount: number, patientName: string, patientIdValue: string) => {
    setPaymentDetailsForModal({
      date: new Date().toLocaleDateString('en-GB'),
      patientName: patientName || 'Patient',
      patientId: patientIdValue || '',
      currency: currency,
      netAmount: netAmount,
    });
    setCollectPaymentOpen(true);
  };
  return (
    <>      
      <HeaderProfile />
    
      <Box sx={{py:3, px:2}} data-testid="checked-in-page-container">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => navigate(`/admin/billing/advance/patient-advance/${patientId}?checked-in`)}>
              <KeyboardBackspaceIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{tBilling('Advance.checked_in')}</Typography>
          </Stack>
          <Stack direction="row" sx={{cursor: 'pointer'}} onClick={() => navigate(`../advance-logs/${patientId}`)} alignItems="center" spacing={1}>

            <ViewIcon/>
            <Typography variant="body2" color="text.secondary" sx={{  fontWeight: 600,  color:'#5A8AEA' }}>
              {tBilling('Advance.view_advance_logs')}
            </Typography>
          </Stack>
        </Stack>
        <Formik
          initialValues={{
            ...advanceInitialValues,
            appointment_visit: Number(appointment_id ?? 0),
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setLatestFormValues(values);
            handleOpenCollectPayment(values.amount ?? 0, `${MainpatientDetails?.first_name} ${MainpatientDetails?.last_name}`, String(patientId));
          }}
        >
          {({ values, handleChange, handleBlur, touched, errors, setFieldValue }) => {
            return (
              <Form>
                <Box>
                  <Grid2 spacing={2} mb={2} container>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <Autocomplete
                        options={(billingConfigAdvance?.ADVANCE_AGAINST ?? []).filter(opt => opt.code !== 'PACKAGE_SERVICE')}
                        value={
                          values.advance_against
                            ? (billingConfigAdvance?.ADVANCE_AGAINST ?? []).find((opt) => opt.code === values.advance_against) || null
                            : null
                        }
                        getOptionLabel={(opt) => opt.name}
                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        onChange={(_, newVal) => {
                          setFieldValue('advance_against', newVal?.code ?? '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label={RequiredLabel(tBilling('Advance.advance_against'), true)}
                            size='small'
                            onBlur={handleBlur}
                            error={touched.advance_against && Boolean(errors.advance_against)}
                            helperText={touched.advance_against && (errors.advance_against as string)}
                          />
                        )}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label={RequiredLabel(tBilling('Advance.date'), true)}
                        type="date"
                        size='small'
                        name="date"
                        disabled
                        fullWidth
                        value={values.date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        slotProps={{
                          inputLabel: { shrink: true }}}
                        error={touched.date && Boolean(errors.date)}
                      
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label={RequiredLabel(tBilling('Advance.amount'), true)}
                        name="amount"
                        size='small'
                        type='number'
                        fullWidth
                        value={values.amount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.amount && Boolean(errors.amount)}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <TextField
                        label={RequiredLabel(tBilling('Advance.remark'), false)}
                        placeholder={tBilling('Advance.type_here')}
                        name="remark"
                        size='small'
                        value={values.remark}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                        maxRows={3}
                        sx={{ mb: 2 }}
                        error={touched.remark && Boolean(errors.remark)}
                        helperText={touched.remark && errors.remark}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} display="flex" justifyContent="flex-end" gap={2}>
                      <Button
                        variant="outlined"
                        sx={{ borderRadius: 3, minWidth: 120 }}
                        type="button"
                        disabled={isSubmittingPayment}
                        onClick={() => navigate(`/admin/billing/advance/patient-advance/${patientId}`)}
                      >
                        {tBilling('package_details.cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        sx={{ borderRadius: 3, minWidth: 120 }}
                        type="submit"
                        disabled={isSubmittingPayment}
                      >
                        {tBilling('Advance.collect_payment')}
                      </Button>
                    </Grid2>
                  </Grid2>
                </Box>
                {paymentDetailsForModal && (
                  <CollectPaymentModal
                    open={collectPaymentOpen}
                    paymentType="ADVANCE"
                    onClose={() => setCollectPaymentOpen(false)}
                    onSave={handlePaymentSave}
                    paymentDetails={paymentDetailsForModal}
                    existingPaymentHistory={[]}
                    walletAdvances={[]}
                    paymentModes={billingConfigAdvance?.modeofpayment || []}
                    hidePaymentModeToggle={true}
                    isSubmitting={isSubmittingPayment}
                    requireFullPayment={true}
                  />
                )}

              </Form>
            );
          }}
        </Formik>
        
      </Box></>
  );
};

export default CheckedInPage;
